import React, { useState, useEffect } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import api from 'helpers/api'
import Button from 'components/button'
import { ITableData, Table } from 'components/table'
import { useApplicationStore } from 'hooks/application'
import Modal from 'components/modal'
import DeleteModal from 'components/delete'
import Menu from 'components/menu'
import { IContextMenuState, IOrganisation, IOrganisationUser } from 'types'
import { Dots, Pencil, People, Delete, Plus, Settings } from 'components/icons'
import Select from 'components/select'
import {
  INITIAL_CONTEXT_MENU_STATE,
  OrganisationRoles,
  PROJECT,
  HUB_CREATION_RESTRICTION_LABEL,
  HUB_CREATION_RESTRICTION_DESCRIPTION,
  SYNC_CONFIGURATION_RESTRICTION_LABEL,
  SYNC_CONFIGURATION_RESTRICTION_DESCRIPTION
} from 'app-constants'
import { capitaliseFirstLetter } from 'helpers/utils'
import { ORGANISATION_TEAMS } from 'routes'
import Input from 'components/input'
import Checkbox from 'components/checkbox'

const SEARCH_DEBOUNCE_MS = 600

type PerPageOption = '50' | '100' | 'all'
const PER_PAGE_OPTIONS: { label: string; value: PerPageOption }[] = [
  { label: '50', value: '50' },
  { label: '100', value: '100' },
  { label: 'All', value: 'all' }
]

const AdminOrganisations: React.FC = () => {
  const { setSnackbarMessage, displayErrorMessage } = useApplicationStore()
  const navigate = useNavigate()

  useEffect(() => {
    document.title = 'Morta | Organisations Panel'
  }, [])

  const blankOrganisation: IOrganisation = {
    publicId: '',
    name: '',
    createdAt: '',
    updatedAt: '',
    enforceLogin: null,
    users: [],
    domains: [],
    hubs: [],
    sites: [],
    isSamlConfigured: false,
    idpCertificate: null,
    idpSsoUrl: null,
    idpIssuer: null,
    enableUserApiKeys: true,
    disablePersonalViewSaving: false,
    restrictHubCreation: false,
    restrictSyncConfiguration: false,
    paidUserSeats: 0,
    planTier: 'professional',
    multiYearContract: false,
    teams: [],
    assignedPaidUsers: 0
  }

  const [organisationModal, setOrganisationModal] = useState<{
    open: boolean
    editing: boolean
    organisation: IOrganisation
  }>({
    open: false,
    editing: false,
    organisation: blankOrganisation
  })

  const [organisationData, setOrganisationData] = useState<IOrganisation[]>([])
  const [search, setSearch] = useState<string>('')
  const [debouncedSearch, setDebouncedSearch] = useState<string>('')
  const [planTierFilter, setPlanTierFilter] = useState<string[]>([])

  const [page, setPage] = useState<number>(1)
  const [refreshKey, setRefreshKey] = useState<number>(0)
  const [total, setTotal] = useState<number>(0)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [perPageOption, setPerPageOption] = useState<PerPageOption>('50')

  const showAll = perPageOption === 'all'
  const perPage = showAll ? 50 : Number.parseInt(perPageOption)

  const [loading, setLoading] = useState<boolean>(false)
  const [deleteTarget, setDeleteTarget] = useState<{ open: boolean; publicId: string; name: string }>({
    open: false,
    publicId: '',
    name: ''
  })

  const [optionsMenu, setOptionsMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [optionsTarget, setOptionsTarget] = useState<IOrganisation | null>(null)

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    setLoading(true)
    api
      .getAdminOrganisations(page, perPage, debouncedSearch, planTierFilter, showAll)
      .then((response) => {
        setOrganisationData(response.data)
        setTotal(response.metadata?.total ?? response.data.length)
        const newTotalPages = response.metadata?.totalPages ?? 1
        setTotalPages(newTotalPages)
        if (page > newTotalPages) setPage(Math.max(1, newTotalPages))
      })
      .catch((e) => {
        displayErrorMessage(e)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [page, perPage, debouncedSearch, planTierFilter, showAll, refreshKey])

  const handleDeleteOrganisation = async () => {
    try {
      await api.deleteOrganisation(deleteTarget.publicId)
      setSnackbarMessage({
        message: 'Organisation deleted successfully',
        status: 'success'
      })
      setRefreshKey((k) => k + 1)
    } catch (e) {
      displayErrorMessage(e)
    }
  }

  const handleUpdateOrganisation = (organisation: IOrganisation) => {
    if (organisation.name === '') {
      displayErrorMessage('Organisation name cannot be empty')
      return
    }

    if (organisation.domains.filter((d) => d.trim() !== '').length === 0) {
      displayErrorMessage('At least one domain must be specified')
      return
    }

    const payload: IOrganisation = {
      ...organisation,
      users: organisation.users
        .filter((u) => (u.user.email || '').trim() !== '')
        .map((u) => ({
          email: u.user.email,
          role: u.role
        })) as unknown as IOrganisationUser[]
    }

    api
      .adminUpdateOrganisation(organisation.publicId, payload)
      .then(() => {
        setOrganisationModal({ open: false, editing: false, organisation: blankOrganisation })
        setSnackbarMessage({
          message: 'Organisation updated successfully',
          status: 'success'
        })
        setRefreshKey((k) => k + 1)
      })
      .catch((e) => {
        displayErrorMessage(e)
      })
  }

  const handleCreateOrganisation = (organisation: IOrganisation) => {
    if (organisation.name === '') {
      displayErrorMessage('Organisation name cannot be empty')
      return
    }

    if (organisation.domains.length === 0) {
      displayErrorMessage('At least one domain must be specified')
      return
    }

    const payload: IOrganisation = {
      ...organisation,
      users: organisation.users.filter((u) => (u.user.email || '').trim() !== '')
    }

    api
      .createOrganisation(payload)
      .then(() => {
        setOrganisationModal({ open: false, editing: false, organisation: blankOrganisation })
        setSnackbarMessage({
          message: 'Organisation created successfully',
          status: 'success'
        })
        setRefreshKey((k) => k + 1)
      })
      .catch((e) => {
        displayErrorMessage(e)
      })
  }

  const addNewUser = () => {
    const newUser: IOrganisationUser = {
      user: {
        email: '',
        name: '',
        publicId: '',
        createdAt: new Date().toISOString(),
        firebaseUserId: '',
        profilePicture: '',
        isOnFreeTrial: false,
        subscriptionLevel: 0,
        userLevelSubscription: 0
      },
      sites: [],
      role: OrganisationRoles.ADMIN
    }

    setOrganisationModal({
      ...organisationModal,
      organisation: {
        ...organisationModal.organisation,
        users: [...organisationModal.organisation.users, newUser]
      }
    })
  }

  const addNewDomain = () => {
    setOrganisationModal((prev) => ({
      ...prev,
      organisation: {
        ...prev.organisation,
        domains: [...prev.organisation.domains, '']
      }
    }))
  }

  const tableData: Array<Record<string, ITableData>> = organisationData.map((organisation) => ({
    id: { label: organisation.publicId, value: organisation.publicId },
    name: { label: organisation.name, value: organisation.name },
    enforceLogin: {
      label: organisation.enforceLogin ? organisation.enforceLogin.toString() : 'No',
      value: organisation.enforceLogin ? organisation.enforceLogin.toString() : 'No'
    },
    domains: {
      label: organisation.domains.join(', '),
      value: organisation.domains.join(', ')
    },
    planTier: {
      label:
        organisation.planTier === 'scale_up' ? 'Scale Up' : organisation.planTier === 'team' ? 'Team' : 'Professional',
      value: organisation.planTier
    },
    isSamlConfigured: {
      label: organisation.isSamlConfigured ? 'Yes' : 'No',
      value: organisation.isSamlConfigured.toString()
    },
    noUsers: { label: organisation.users.length.toString(), value: organisation.users.length.toString() },
    paidUserSeats: {
      label: organisation.paidUserSeats?.toString() || '0',
      value: organisation.paidUserSeats?.toString() || '0'
    },
    assignedPaidUsers: {
      label: organisation.assignedPaidUsers?.toString() || '0',
      value: organisation.assignedPaidUsers?.toString() || '0'
    },
    multiYearContract: {
      label: organisation.planTier === 'scale_up' ? (organisation.multiYearContract ? 'Yes' : 'No') : '-',
      value: organisation.multiYearContract ? 'Yes' : 'No'
    },
    noHubs: { label: organisation.hubs.length.toString(), value: organisation.hubs.length.toString() },
    noSites: {
      label: organisation.sites.length.toString(),
      value: organisation.sites.length.toString()
    },
    options: {
      label: (
        <Button
          internalType="transparent"
          aria-label={`Open options for ${organisation.name}`}
          onClick={(event) => {
            const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
            setOptionsTarget(organisation)
            setOptionsMenu({
              open: true,
              top: `${rect.bottom + 4}px`,
              left: `${rect.right - 200}px`,
              right: 'auto',
              bottom: 'auto'
            })
          }}
        >
          <Dots />
        </Button>
      ),
      value: organisation.name
    }
  }))

  return (
    <div className="w-full h-full flex flex-col overflow-hidden p-5">
      <div className="flex flex-col md:flex-row w-full md:items-center gap-2.5">
        <Input
          type="text"
          placeholder="Search by name or domain..."
          className="w-full md:w-[300px]"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          className="w-full md:w-[200px] h-[44px]"
          multiselect
          placeholder="All Plan Tiers"
          options={[
            { label: 'Professional', value: 'professional' },
            { label: 'Team', value: 'team' },
            { label: 'Scale Up', value: 'scale_up' }
          ]}
          optionsSelected={planTierFilter}
          setOptionsSelected={(values) => {
            setPlanTierFilter(values)
            setPage(1)
          }}
          onOptionClick={(value) => {
            setPlanTierFilter((prev) => (prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]))
            setPage(1)
          }}
        />
        <span className="text-sm text-muted">{total} organisations</span>
        <Button
          className="md:ml-auto h-full"
          internalType="accept"
          onClick={() => setOrganisationModal({ open: true, editing: false, organisation: blankOrganisation })}
        >
          <Plus className="xl:mr-[5px]" />
          <span className="hidden xl:inline">New Organisation</span>
        </Button>
      </div>

      <div className="w-full flex flex-col select-none mt-5 overflow-auto flex-1">
        <Table
          data={tableData}
          include={[
            { header: 'Name', id: 'name' },
            { header: 'Enforce Login', id: 'enforceLogin' },
            { header: 'Domains', id: 'domains' },
            { header: 'Plan Tier', id: 'planTier' },
            { header: '# of Admins', id: 'noUsers' },
            { header: `# of ${capitaliseFirstLetter(PROJECT)}s`, id: 'noHubs' },
            { header: '# of Sites', id: 'noSites' },
            { header: 'Paid User Seats', id: 'paidUserSeats' },
            { header: 'Assigned Paid Users', id: 'assignedPaidUsers' },
            { header: 'SAML Configured', id: 'isSamlConfigured' },
            { header: 'Multi-Year Contract', id: 'multiYearContract' },
            {
              header: (
                <div className="w-full flex items-center justify-center">
                  <Settings className="size-4" />
                </div>
              ),
              id: 'options'
            }
          ]}
          defaultSort={'name'}
          defaultSortAscending={true}
          loading={loading}
          stickyHeader
        />
      </div>

      <div className="flex flex-row flex-wrap items-center justify-start gap-2.5 mt-2.5">
        <span className="text-sm text-muted">Rows per page</span>
        <Select
          className="w-[100px] h-full py-[7px]"
          options={PER_PAGE_OPTIONS}
          optionsSelected={[perPageOption]}
          onOptionClick={(value) => {
            setPerPageOption(value as PerPageOption)
            setPage(1)
          }}
        />
        <Button
          internalType="outline"
          disabled={page <= 1 || loading}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          Prev
        </Button>
        <span className="text-sm text-muted">
          Page {page} of {totalPages}
        </span>
        <Button internalType="outline" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}>
          Next
        </Button>
        <span className="text-sm text-muted">
          ({total === 0 ? 0 : (page - 1) * perPage + 1}-{showAll ? total : Math.min(page * perPage, total)} of {total})
        </span>
      </div>

      {organisationModal && organisationModal.open && (
        <Modal
          title={organisationModal.editing ? 'Edit Organisation' : 'Create Organisation'}
          id={organisationModal.editing ? 'edit-organisation-modal' : 'create-organisation-modal'}
          open={organisationModal.open}
          setOpen={(open) => {
            if (open) {
              setOrganisationModal({ ...organisationModal, open: true })
            } else {
              setOrganisationModal({ open: false, editing: false, organisation: blankOrganisation })
            }
          }}
        >
          <form
            onSubmit={(event) => {
              event.preventDefault()
              if (organisationModal.editing) {
                handleUpdateOrganisation(organisationModal.organisation)
              } else {
                handleCreateOrganisation(organisationModal.organisation)
              }
            }}
            className="flex flex-col w-full gap-5"
          >
            <div>
              <label htmlFor="organisation-name" className="block text-sm text-muted mb-[5px]">
                Name
              </label>
              <Input
                id="organisation-name"
                type="text"
                placeholder="Organisation Name"
                value={organisationModal.organisation.name}
                onChange={(event) =>
                  setOrganisationModal({
                    ...organisationModal,
                    organisation: { ...organisationModal.organisation, name: event.target.value }
                  })
                }
              />
            </div>

            <div>
              <label htmlFor="organisation-domains" className="block text-sm text-muted mb-[5px]">
                Organisation Domains
              </label>

              {organisationModal.organisation.domains.map((domain, index) => (
                <div key={index} className="flex flex-row items-center mb-2 gap-2.5">
                  <Input
                    id={`organisation-domain-${index}`}
                    type="text"
                    placeholder="example.com"
                    value={domain}
                    className="mr-2 flex-1"
                    onChange={(event) => {
                      const updated = [...organisationModal.organisation.domains]
                      updated[index] = event.target.value
                      setOrganisationModal({
                        ...organisationModal,
                        organisation: { ...organisationModal.organisation, domains: updated }
                      })
                    }}
                  />

                  <Button
                    internalType="danger"
                    onClick={() => {
                      const updated = organisationModal.organisation.domains.filter((_, i) => i !== index)
                      setOrganisationModal({
                        ...organisationModal,
                        organisation: { ...organisationModal.organisation, domains: updated }
                      })
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}

              <Button internalType="accept" onClick={addNewDomain}>
                <Plus className="mr-[5px]" />
                Add Domain
              </Button>
            </div>

            <div>
              <label htmlFor="organisation-users" className="block text-sm text-muted mb-[5px]">
                Organisation Admins
              </label>

              {organisationModal.organisation.users.map((user, index) => {
                const isServiceAccount = organisationModal.organisation.serviceAccount?.email === user.user.email
                return (
                  <div key={index} className="flex flex-row items-center mb-2 gap-2.5">
                    <Input
                      id={`organisation-user-${index}`}
                      type="email"
                      placeholder="User Email"
                      value={user.user.email}
                      className="mr-2 flex-1"
                      disabled={isServiceAccount}
                      onChange={(event) => {
                        const updatedUsers = [...organisationModal.organisation.users]
                        updatedUsers[index].user.email = event.target.value
                        setOrganisationModal({
                          ...organisationModal,
                          organisation: {
                            ...organisationModal.organisation,
                            users: updatedUsers
                          }
                        })
                      }}
                    />

                    {isServiceAccount ? (
                      <span className="text-xs text-muted italic whitespace-nowrap ml-2">Service Account</span>
                    ) : (
                      <Button
                        internalType="danger"
                        onClick={() => {
                          const updatedUsers = organisationModal.organisation.users.filter((_, i) => i !== index)
                          setOrganisationModal({
                            ...organisationModal,
                            organisation: {
                              ...organisationModal.organisation,
                              users: updatedUsers
                            }
                          })
                        }}
                        className="ml-2"
                      >
                        Remove User
                      </Button>
                    )}
                  </div>
                )
              })}

              <Button internalType="accept" onClick={() => addNewUser()}>
                <Plus className="mr-[5px]" />
                Add User
              </Button>
            </div>

            <Select
              label="Login Provider Enforcement (e.g. must login with Google)"
              optionsSelected={
                organisationModal.organisation.enforceLogin ? [organisationModal.organisation.enforceLogin] : ['null']
              }
              options={[
                { label: 'Any', value: 'null' },
                { label: 'Email/Password', value: 'morta.io' },
                { label: 'Google', value: 'google.com' },
                { label: 'Microsoft', value: 'microsoft.com' },
                { label: 'SAML', value: 'saml.morta.io' }
              ]}
              onOptionClick={(option) => {
                let value: string | null = option

                if (option === 'null') {
                  value = null
                }

                setOrganisationModal({
                  ...organisationModal,
                  organisation: {
                    ...organisationModal.organisation,
                    enforceLogin: value
                  }
                })
              }}
            />

            <Select
              label="Plan Tier"
              optionsSelected={[organisationModal.organisation.planTier || 'professional']}
              options={[
                { label: 'Professional', value: 'professional' },
                { label: 'Team', value: 'team' },
                { label: 'Scale Up', value: 'scale_up' }
              ]}
              onOptionClick={(option) => {
                const planTier = option as IOrganisation['planTier']
                setOrganisationModal({
                  ...organisationModal,
                  organisation: {
                    ...organisationModal.organisation,
                    planTier,
                    multiYearContract:
                      planTier === 'scale_up' ? organisationModal.organisation.multiYearContract : false,
                    restrictHubCreation:
                      planTier === 'scale_up' ? organisationModal.organisation.restrictHubCreation : false,
                    restrictSyncConfiguration:
                      planTier === 'scale_up' ? organisationModal.organisation.restrictSyncConfiguration : false
                  }
                })
              }}
            />

            <div className="flex flex-col gap-[5px]">
              <div className="block text-sm text-muted mb-[5px]">Personal Views</div>
              <Checkbox
                checked={organisationModal.organisation.disablePersonalViewSaving}
                onChange={(event) =>
                  setOrganisationModal({
                    ...organisationModal,
                    organisation: {
                      ...organisationModal.organisation,
                      disablePersonalViewSaving: event.target.checked
                    }
                  })
                }
              >
                Disable &ldquo;Save Personal View&rdquo; for unlicensed members across all {PROJECT}s in this
                organisation
              </Checkbox>
              <div className="text-xs text-muted mt-[5px]">
                Unlicensed members can still apply filters, groups and sorting temporarily, but won&rsquo;t be able to
                save them for future use.
              </div>
              <div className="text-xs text-warning mt-[5px]">
                You&rsquo;ll also lose visibility into how these members are manipulating and viewing the data.
              </div>
            </div>

            {organisationModal.organisation.planTier === 'scale_up' && (
              <div className="flex flex-col gap-[5px]">
                <div className="block text-sm text-muted mb-[5px]">{capitaliseFirstLetter(PROJECT)} Creation</div>
                <Checkbox
                  checked={organisationModal.organisation.restrictHubCreation}
                  onChange={(event) =>
                    setOrganisationModal({
                      ...organisationModal,
                      organisation: {
                        ...organisationModal.organisation,
                        restrictHubCreation: event.target.checked
                      }
                    })
                  }
                >
                  {HUB_CREATION_RESTRICTION_LABEL}
                </Checkbox>
                <div className="text-xs text-muted mt-[5px]">{HUB_CREATION_RESTRICTION_DESCRIPTION}</div>
              </div>
            )}

            {organisationModal.organisation.planTier === 'scale_up' && (
              <div className="flex flex-col gap-[5px]">
                <div className="block text-sm text-muted mb-[5px]">Sync Configuration</div>
                <Checkbox
                  checked={organisationModal.organisation.restrictSyncConfiguration}
                  onChange={(event) =>
                    setOrganisationModal({
                      ...organisationModal,
                      organisation: {
                        ...organisationModal.organisation,
                        restrictSyncConfiguration: event.target.checked
                      }
                    })
                  }
                >
                  {SYNC_CONFIGURATION_RESTRICTION_LABEL}
                </Checkbox>
                <div className="text-xs text-muted mt-[5px]">{SYNC_CONFIGURATION_RESTRICTION_DESCRIPTION}</div>
              </div>
            )}

            {organisationModal.organisation.planTier === 'scale_up' && (
              <Select
                label="Multi-Year Contract"
                optionsSelected={organisationModal.organisation.multiYearContract ? ['true'] : ['false']}
                options={[
                  { label: 'Yes', value: 'true' },
                  { label: 'No', value: 'false' }
                ]}
                onOptionClick={(option) => {
                  setOrganisationModal({
                    ...organisationModal,
                    organisation: {
                      ...organisationModal.organisation,
                      multiYearContract: option === 'true'
                    }
                  })
                }}
              />
            )}

            <div>
              <label htmlFor="idp-sso-url" className="block text-sm text-muted mb-[5px]">
                Identity Provider Single Sign-On URL (IDP SSO URL)
              </label>
              <Input
                id="idp-sso-url"
                type="text"
                placeholder="e.g. https://dev-83990839.okta.com/app/dev-83990839_mortaproduction_1/exkpjaluq3jD1uvTD5d7/sso/saml"
                value={organisationModal.organisation.idpSsoUrl || ''}
                onChange={(event) =>
                  setOrganisationModal({
                    ...organisationModal,
                    organisation: {
                      ...organisationModal.organisation,
                      idpSsoUrl: event.target.value
                    }
                  })
                }
              />
            </div>

            <div>
              <label htmlFor="idp-issuer" className="block text-sm text-muted mb-[5px]">
                Identity Provider Issuer
              </label>
              <Input
                id="idp-issuer"
                type="text"
                placeholder="e.g. http://www.okta.com/exkpjaluq3jD1uvTD5d7"
                value={organisationModal.organisation.idpIssuer || ''}
                onChange={(event) =>
                  setOrganisationModal({
                    ...organisationModal,
                    organisation: {
                      ...organisationModal.organisation,
                      idpIssuer: event.target.value
                    }
                  })
                }
              />
            </div>

            <div>
              <label htmlFor="idp-certificate" className="block text-sm text-muted mb-[5px]">
                IDP SSO Certificate (X.509 Certificate)
              </label>
              <textarea
                id="idp-certificate"
                placeholder="-----BEGIN CERTIFICATE-----..."
                value={organisationModal.organisation.idpCertificate || ''}
                className="w-full text-sm rounded-lg bg-surface-faint border-divider"
                onChange={(event) =>
                  setOrganisationModal({
                    ...organisationModal,
                    organisation: {
                      ...organisationModal.organisation,
                      idpCertificate: event.target.value
                    }
                  })
                }
                rows={10}
              />
            </div>

            <div className="flex items-center justify-end">
              <Button
                type="button"
                className="ml-2.5"
                internalType="danger"
                onClick={() => setOrganisationModal({ open: false, editing: false, organisation: blankOrganisation })}
              >
                Cancel
              </Button>
              <Button type="submit" className="ml-2.5" internalType="accept">
                {organisationModal.editing ? 'Update Organisation' : 'Create Organisation'}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      <DeleteModal
        id="delete-organisation-modal"
        open={deleteTarget.open}
        setOpen={(open) => setDeleteTarget((prev) => ({ ...prev, open }))}
        resourceName={deleteTarget.name}
        deleteResource={handleDeleteOrganisation}
        notUndo
        onError={(e) => displayErrorMessage(e)}
      />

      <Menu
        id="admin-organisation-options-menu"
        menuState={optionsMenu}
        setMenuState={setOptionsMenu}
        width={200}
        onClose={() => setOptionsTarget(null)}
      >
        <div className="flex flex-col py-1">
          <div
            className="flex flex-row items-center gap-2.5 px-3 py-2 hover:bg-surface-sunken text-left cursor-pointer"
            onClick={() => {
              if (!optionsTarget) return
              setOrganisationModal({ open: true, organisation: optionsTarget, editing: true })
              setOptionsMenu(INITIAL_CONTEXT_MENU_STATE)
              setOptionsTarget(null)
            }}
          >
            <Pencil />
            Edit
          </div>
          <div
            className="flex flex-row items-center gap-2.5 px-3 py-2 hover:bg-surface-sunken text-left cursor-pointer"
            onClick={() => {
              if (!optionsTarget) return
              navigate(generatePath(ORGANISATION_TEAMS, { organisationId: optionsTarget.publicId }))
              setOptionsMenu(INITIAL_CONTEXT_MENU_STATE)
              setOptionsTarget(null)
            }}
          >
            <People />
            Manage Teams
          </div>
          <div
            className="flex flex-row items-center gap-2.5 px-3 py-2 hover:bg-surface-sunken text-left text-red cursor-pointer"
            onClick={() => {
              if (!optionsTarget) return
              setDeleteTarget({ open: true, publicId: optionsTarget.publicId, name: optionsTarget.name })
              setOptionsMenu(INITIAL_CONTEXT_MENU_STATE)
              setOptionsTarget(null)
            }}
          >
            <Delete />
            Delete
          </div>
        </div>
      </Menu>
    </div>
  )
}

export default AdminOrganisations
